import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import type {CrmOptionType} from 'nutshell-graphql-types';

import {Button} from '../button/button';
import {FlashBanner} from '../flash-banner/flash-banner';
// @ts-expect-error - imported from .js/.jsx file
import {ExternalLinkIcon} from '../icon/svg/external-link-icon';
import {GreySection} from '../layout/grey-section';
import {Stack} from '../layout/stack/stack';
import {StackLegacy} from '../layout/stack/stack-legacy';
import {Link} from '../link/link';
import {NumberedBreadcrumbs} from '../numbered-breadcrumbs/numbered-breadcrumbs';
import {Body} from '../typography/body';

import {getStatusPageInfoFromImport2Import} from './helpers';
import type {Import2StatusFragment} from './types';

import styles from './status-page.module.css';

type Props = {
    importStatus: Import2StatusFragment;
    isSupportLogin?: boolean;
    approveImport2FullMigration: (originId: string) => Promise<any>;
    requestImport2Sponsorship: (originId: string) => Promise<any>;
    refetchImportStatus: () => Promise<void>;
    getLinkToImports: (type: string, originId: string) => string;
    onInitiateImport: (crmType: CrmOptionType) => void;
};

export function StatusPage(props: Props) {
    const {
        importStatus,
        isSupportLogin,
        approveImport2FullMigration,
        requestImport2Sponsorship,
        refetchImportStatus,
        getLinkToImports,
    } = props;

    const [activeStep, setActiveStep] = React.useState('full-complete');
    React.useEffect(() => {
        // Status 'new' or 'sample in progress'
        if (
            importStatus.importStatus === 'SAMPLE_NEW' ||
            importStatus.importStatus === 'SAMPLE_BEGAN'
        ) {
            setActiveStep('start-sample');
        }
        // Status 'sample complete'
        if (importStatus.importStatus === 'SAMPLE_COMPLETE' && !importStatus.reviewApproved) {
            setActiveStep('request-approval');
        }
        if (
            // Statuses 'migration ready' and 'migration in progress' and 'migration complete'
            importStatus.importStatus === 'MIGRATION_READY' ||
            (importStatus.importStatus === 'MIGRATION_BEGAN' && importStatus.reviewApproved)
        ) {
            setActiveStep('full-complete');
        }
    }, [importStatus]);

    const shouldShowSupportApprovalButton =
        isSupportLogin &&
        importStatus &&
        importStatus.reviewRequested &&
        !importStatus.reviewApproved;
    const shouldShowRequestApprovalButton =
        importStatus.importStatus === 'SAMPLE_COMPLETE' &&
        !importStatus.reviewRequested &&
        !importStatus.reviewApproved;
    const isImportComplete = importStatus.importStatus === 'COMPLETED';
    const isReverted = importStatus.import2Status === 'STATUS_REVERTED';

    const {icon, pageInfo, pageSubtitle, breadcrumbSubtitle, buttonCopy} =
        // @ts-expect-error
        getStatusPageInfoFromImport2Import(importStatus);

    return (
        <div className={styles.container}>
            <GreySection
                content={
                    <StackLegacy spacing={32}>
                        <div>
                            <div className='mb-4'>
                                <Body as='div'>
                                    Your Import2 status from {props.importStatus.crmOption.label}
                                </Body>
                            </div>
                            <Link
                                href={importStatus.redirectUrl}
                                newTab={true}
                                variant='primary'
                                size='small'
                            >
                                <span className='mr-4'>Open migration in Import2</span>
                                <ExternalLinkIcon size={10} wrapWithDiv={false} />
                            </Link>
                        </div>
                        <NumberedBreadcrumbs
                            breadcrumbs={[
                                {
                                    id: 'start-sample',
                                    title: 'Start a sample import from your chosen CRM',
                                    subtitle:
                                        activeStep === 'start-sample'
                                            ? breadcrumbSubtitle
                                            : undefined,
                                },
                                {
                                    id: 'request-approval',
                                    title: 'Check out your sample and request approval from Nutshell for a full migration',
                                    subtitle:
                                        activeStep === 'request-approval'
                                            ? breadcrumbSubtitle
                                            : undefined,
                                },
                                {
                                    id: 'full-complete',
                                    title: 'Kick off a full migration in Import2',
                                    subtitle:
                                        activeStep === 'full-complete'
                                            ? breadcrumbSubtitle
                                            : undefined,
                                },
                            ]}
                            selectedValue={activeStep}
                            isHorizontal={false}
                            spacing={24}
                            useCompleteSubtitle={true}
                            isLastBreadcrumbComplete={isImportComplete}
                            titleSize='normal'
                        />
                        {isImportComplete && (
                            <Stack spacing={8}>
                                <Body size='small'>
                                    You’re done! If there’s a problem, you can revert your import
                                    in&nbsp;
                                    <Link
                                        href={importStatus.redirectUrl}
                                        newTab={true}
                                        variant='primary'
                                        size='small'
                                    >
                                        Import2
                                    </Link>
                                    .
                                </Body>
                                <Body size='small'>
                                    Need to make a supplemental import from{' '}
                                    {importStatus.crmOption.label}?&nbsp;
                                    <Link
                                        onClick={() => {
                                            props.onInitiateImport(importStatus.crmOption.type);
                                        }}
                                        newTab={true}
                                        variant='primary'
                                        size='small'
                                    >
                                        Create a new import
                                    </Link>
                                </Body>
                            </Stack>
                        )}
                    </StackLegacy>
                }
                isFullHeight={true}
            />
            <div className={styles['button-section']}>
                {isReverted && (
                    <FlashBanner type='info' hasBorder={true}>
                        <div className='inline-block'>
                            <span className='font-bold'>
                                It looks like your import is in a reverted state. Don’t worry!
                            </span>{' '}
                            If this is unexpected, note that this is a normal step in the Import2
                            migration process and will be resolved as the import continues. If this
                            state continues for longer than a day, please contact support.{' '}
                            <Link href={importStatus.redirectUrl} newTab={true} variant='primary'>
                                See current authoritative status in Import2
                            </Link>
                        </div>
                    </FlashBanner>
                )}
                {icon}
                <Body>{pageInfo}</Body>
                <div className={styles.subtitle}>{pageSubtitle}</div>
                {shouldShowRequestApprovalButton && (
                    <Button
                        variant='primary'
                        size='big'
                        onClick={() => {
                            requestImport2Sponsorship(importStatus.originId).then(
                                refetchImportStatus
                            );
                        }}
                    >
                        Request approval for a full migration
                    </Button>
                )}
                {buttonCopy && (
                    <Link
                        href={importStatus.redirectUrl}
                        newTab={true}
                        variant='button-primary'
                        size='big'
                    >
                        {buttonCopy}
                    </Link>
                )}
                {shouldShowSupportApprovalButton && (
                    <Button
                        variant='primary'
                        size='big'
                        onClick={() => {
                            approveImport2FullMigration(importStatus.originId).then(
                                refetchImportStatus
                            );
                        }}
                    >
                        Support only: Approve full migration
                    </Button>
                )}
                {importStatus.import2Status === 'STATUS_MIGRATION_COMPLETE' && (
                    <Body>
                        See your imported{' '}
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            variant='primary'
                            to={getLinkToImports('people', importStatus.originId)}
                        >
                            people
                        </Link>
                        ,{' '}
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            variant='primary'
                            to={getLinkToImports('companies', importStatus.originId)}
                        >
                            companies
                        </Link>
                        , and{' '}
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            variant='primary'
                            to={getLinkToImports('leads', importStatus.originId)}
                        >
                            leads
                        </Link>
                    </Body>
                )}
            </div>
        </div>
    );
}
